import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Container,
  Paper,
  Fade,
  Slide,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  VpnKey as KeyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { LoaderWrapper } from './index';

// Validation schema
const schema = yup.object({
  newPassword: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    setMounted(true);
    
    // Get reset token from URL
    const token = searchParams.get('token');
    if (!token) {
      toast.error('Invalid reset link. Please request a new password reset.');
      navigate('/main/forgot-password');
    } else {
      setResetToken(token);
    }
  }, [searchParams, navigate]);

  const onSubmit = async (data) => {
    if (!resetToken) {
      toast.error('Invalid reset token. Please request a new password reset.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/reset-password`,
        {
          resetToken: resetToken,
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
        }
      );

      if (response.data.success) {
        toast.success('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/main/login');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to reset password');
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          animation: 'float 20s ease-in-out infinite',
        },
      }}
    >
      <Container maxWidth="sm">
        <Fade in={mounted} timeout={1000}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              transform: mounted ? 'translateY(0)' : 'translateY(50px)',
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                color: 'white',
                p: 4,
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #06b6d4)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s ease-in-out infinite',
                },
              }}
            >
              <Slide direction="down" in={mounted} timeout={800}>
                <Box>
                  <KeyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography
                    variant="h4"
                    component="h1"
                    fontWeight="bold"
                    sx={{
                      mb: 1,
                      background: 'linear-gradient(45deg, #ffffff, #e0f2fe)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    Reset Password
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Create a new password for your account
                  </Typography>
                </Box>
              </Slide>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Slide direction="up" in={mounted} timeout={1000}>
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                    <Typography
                      variant="h6"
                      component="h2"
                      fontWeight="600"
                      color="primary"
                      gutterBottom
                    >
                      OTP Verified Successfully
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please enter your new password below
                    </Typography>
                  </Box>

                  {/* Error Alert */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.2rem',
                        },
                      }}
                      onClose={() => setError('')}
                    >
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ mb: 3 }}>
                      <Controller
                        name="newPassword"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="New Password"
                            type={showNewPassword ? 'text' : 'password'}
                            variant="outlined"
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                                '&.Mui-focused': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                },
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: '#1e3c72' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end"
                                    sx={{
                                      color: '#1e3c72',
                                      '&:hover': {
                                        backgroundColor: '#e3f2fd',
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ mb: 4 }}>
                      <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Confirm New Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            variant="outlined"
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                                '&.Mui-focused': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                },
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: '#1e3c72' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    sx={{
                                      color: '#1e3c72',
                                      '&:hover': {
                                        backgroundColor: '#e3f2fd',
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>

                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>Password Requirements:</strong>
                      </Typography>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        <li>Minimum 8 characters</li>
                        <li>Mix of uppercase and lowercase letters</li>
                        <li>Include numbers and special characters</li>
                      </ul>
                    </Alert>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        mb: 2,
                        py: 1.5,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                        boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                        },
                      }}
                    >
                      <LoaderWrapper loading={loading} type="button">
                        {loading ? 'Resetting Password...' : 'Reset Password'}
                      </LoaderWrapper>
                    </Button>
                  </form>

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Link
                      component={RouterLink}
                      to="/login"
                      variant="body2"
                      sx={{
                        display: 'inline-block',
                        p: 1,
                        borderRadius: 1,
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        color: '#1e3c72',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          color: '#1e3c72',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Back to Login
                    </Link>
                  </Box>
                </Box>
              </Slide>
            </CardContent>
          </Paper>
        </Fade>
      </Container>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Box>
  );
};

export default ResetPasswordPage;
